import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import MultiFileInput from '../components/MultiFileInput';
import {
  FiUser,
  FiHome,
  FiFileText,
  FiBriefcase,
  FiUsers,
  FiTarget,
  FiShield,
  FiCreditCard,
  FiPackage,
  FiCoffee,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiSave,
  FiAlertCircle,
} from 'react-icons/fi';

const steps = [
  { id: 0, title: 'Personal Details', icon: FiUser, color: 'from-blue-500 to-blue-600' },
  { id: 1, title: 'Business Details', icon: FiHome, color: 'from-purple-500 to-purple-600' },
  { id: 2, title: 'Registration Info', icon: FiFileText, color: 'from-green-500 to-green-600' },
  { id: 3, title: 'Experience', icon: FiBriefcase, color: 'from-orange-500 to-orange-600' },
  { id: 4, title: 'References', icon: FiUsers, color: 'from-pink-500 to-pink-600' },
  { id: 5, title: 'Scope of Works', icon: FiTarget, color: 'from-indigo-500 to-indigo-600' },
  { id: 6, title: 'CSCS Registration', icon: FiShield, color: 'from-red-500 to-red-600' },
  { id: 7, title: 'Payment Info', icon: FiCreditCard, color: 'from-emerald-500 to-emerald-600' },
  { id: 8, title: 'Insurances', icon: FiShield, color: 'from-cyan-500 to-cyan-600' },
  { id: 9, title: 'Clothing Sizes', icon: FiPackage, color: 'from-yellow-500 to-yellow-600' },
  { id: 10, title: 'How do you like it?', icon: FiCoffee, color: 'from-amber-500 to-amber-600' },
];

const OnboardingPage = () => {
  const { onboardingComplete } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    full_name: '',
    address: '',
    telephone: '',
    email: '',
    business_name: '',
    business_address: '',
    business_type: '',
    cis_registration: '',
    utr: '',
    national_insurance: '',
    time_served: '',
    trades: [],
    trade_qualifications: '',
    trades_competency: '',
    references_text: '',
    proof_of_work: '',
    socials: '',
    preferred_project_type: '',
    max_project_value: '',
    scope_day_rate: '',
    pricing_preference: 'day_rate',
    cscs_registration: '',
    cscs_card_number: '',
    bank_name: '',
    sort_code: '',
    account_number: '',
    name_on_account: '',
    minimum_job_value: '',
    maximum_job_value: '',
    day_rate: '',
    insurances: [],
    qualifying_bodies: [],
    waist_size: '',
    jumper_size: '',
    tshirt_size: '',
    gillet_size: '',
    preferred_drink: '',
    drink_notes: '',
  });

  useEffect(() => {
    if (onboardingComplete) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [onboardingComplete, navigate]);

  const loadData = async () => {
    try {
      const [profileData, tradesData] = await Promise.all([
        api.getMyProfile(),
        api.getTrades(),
      ]);

      if (profileData.contractor) {
        const contractor = profileData.contractor;
        setFormData({
          full_name: contractor.full_name || '',
          address: contractor.address || '',
          telephone: contractor.telephone || '',
          email: contractor.email || '',
          business_name: contractor.business_name || '',
          business_address: contractor.business_address || '',
          business_type: contractor.business_type || '',
          cis_registration: contractor.cis_registration || '',
          utr: contractor.utr || '',
          national_insurance: contractor.national_insurance || '',
          time_served: contractor.time_served || '',
          trades: contractor.trades?.map((t) => t.id) || [],
          trade_qualifications: contractor.trade_qualifications || '',
          trades_competency: contractor.trades_competency || '',
          references_text: contractor.references_text || '',
          proof_of_work: contractor.proof_of_work || '',
          socials: contractor.socials || '',
          preferred_project_type: contractor.preferred_project_type || '',
          max_project_value: contractor.max_project_value || '',
          scope_day_rate: contractor.scope_day_rate || '',
          pricing_preference: contractor.pricing_preference || 'day_rate',
          cscs_registration: contractor.cscs_registration || '',
          cscs_card_number: contractor.cscs_card_number || '',
          bank_name: contractor.bank_name || '',
          sort_code: contractor.sort_code || '',
          account_number: contractor.account_number || '',
          name_on_account: contractor.name_on_account || '',
          minimum_job_value: contractor.minimum_job_value || '',
          maximum_job_value: contractor.maximum_job_value || '',
          day_rate: contractor.day_rate || '',
          insurances: [],
          qualifying_bodies: [],
          waist_size: contractor.waist_size || '',
          jumper_size: contractor.jumper_size || '',
          tshirt_size: contractor.tshirt_size || '',
          gillet_size: contractor.gillet_size || '',
          preferred_drink: contractor.preferred_drink || '',
          drink_notes: contractor.drink_notes || '',
        });
      }

      setTrades(tradesData.trades || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Failed to load your profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files, type } = e.target;

    if (type === 'file') {
      if (name === 'insurances' || name === 'qualifying_bodies') {
        const added = files ? Array.from(files) : [];
        setFormData((prev) => {
          const prevFiles = Array.isArray(prev[name]) ? prev[name] : [];
          const merged = [...prevFiles, ...added].slice(0, 10);
          return { ...prev, [name]: merged };
        });
        e.target.value = '';
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: files?.[0] || null,
        }));
      }
    } else if (type === 'checkbox') {
      const tradeId = parseInt(value);
      setFormData((prev) => ({
        ...prev,
        trades: prev.trades.includes(tradeId)
          ? prev.trades.filter((id) => id !== tradeId)
          : [...prev.trades, tradeId],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const saveProgress = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const formDataToSend = new FormData();

      Object.keys(formData).forEach((key) => {
        const value = formData[key];
        if (value !== null && value !== undefined && value !== '') {
          if (key === 'trades' && Array.isArray(value)) {
            formDataToSend.append('trades', JSON.stringify(value));
          } else if (key === 'insurances' && Array.isArray(value)) {
            value.forEach((file) => formDataToSend.append('insurances', file));
          } else if (key === 'qualifying_bodies' && Array.isArray(value)) {
            value.forEach((file) => formDataToSend.append('qualifying_bodies', file));
          } else if (value instanceof File) {
            formDataToSend.append(key, value);
          } else {
            formDataToSend.append(key, value);
          }
        }
      });

      await api.saveProfile(formDataToSend);
      setSuccess('Progress saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message || 'Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      await saveProgress();
      await api.submitOnboarding();
      navigate('/dashboard');
    } catch (error) {
      setError(error.message || 'Failed to submit onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  const checkStepCompletion = useCallback(
    (stepIndex) => {
      switch (stepIndex) {
        case 0:
          return (
            formData.full_name &&
            formData.address &&
            formData.telephone &&
            formData.email
          );
        case 1:
          return (
            formData.business_name &&
            formData.business_address &&
            formData.business_type
          );
        case 2:
          return (
            formData.utr &&
            formData.national_insurance
          );
        case 3:
          return (
            formData.time_served ||
            formData.trade_qualifications ||
            formData.trades_competency ||
            formData.trades.length > 0
          );
        case 4:
          return (
            formData.references_text ||
            formData.proof_of_work ||
            formData.socials
          );
        case 5:
          return (
            formData.preferred_project_type ||
            formData.max_project_value ||
            (formData.pricing_preference === 'day_rate' ? formData.scope_day_rate : true)
          );
        case 6:
          return formData.cscs_registration || formData.cscs_card_number;
        case 7:
          return (
            formData.bank_name &&
            formData.sort_code &&
            formData.account_number &&
            formData.name_on_account &&
            formData.minimum_job_value &&
            formData.maximum_job_value &&
            formData.day_rate
          );
        case 8:
          return Array.isArray(formData.insurances) && formData.insurances.length > 0;
        case 9:
          return (
            formData.waist_size ||
            formData.jumper_size ||
            formData.tshirt_size ||
            formData.gillet_size
          );
        case 10:
          return true;
        default:
          return false;
      }
    },
    [formData]
  );

  const canSubmit = () => {
    return (
      checkStepCompletion(0) &&
      checkStepCompletion(1) &&
      checkStepCompletion(2) &&
      checkStepCompletion(7) &&
      checkStepCompletion(8)
    );
  };

  const renderFormStep = () => {
    const currentStepData = steps[currentStep];
    const IconComponent = currentStepData.icon;

    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center shadow-lg`}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-slate-300">Tell us about yourself</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Full Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Address <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your address"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Telephone <span className="text-red-400">*</span>
                </label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your phone number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your email address"
                  required
                />
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center shadow-lg`}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-slate-300">Your business information</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Business Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your business name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Business Address <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="business_address"
                  value={formData.business_address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your business address"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Business Type <span className="text-red-400">*</span>
                </label>
                <select
                  name="business_type"
                  value={formData.business_type}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="">Select Business Type</option>
                  <option value="Sole Trader">Sole Trader</option>
                  <option value="Limited Company">Limited Company</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center shadow-lg`}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-slate-300">Registration and tax information</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  C.I.S Registration
                </label>
                <input
                  type="text"
                  name="cis_registration"
                  value={formData.cis_registration}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your CIS registration number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  U.T.R <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="utr"
                  value={formData.utr}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your UTR number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  National Insurance Number <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="national_insurance"
                  value={formData.national_insurance}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your National Insurance number"
                  required
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center shadow-lg`}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-slate-300">Your professional background</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Trades
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto p-4 bg-white/5 rounded-lg border border-white/10">
                  {trades.map((trade) => (
                    <label
                      key={trade.id}
                      className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-white/10"
                    >
                      <input
                        type="checkbox"
                        value={trade.id}
                        checked={formData.trades.includes(trade.id)}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-500 rounded focus:ring-blue-500"
                      />
                      <span className="text-white text-sm">{trade.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Time Served in Industry
                </label>
                <input
                  type="text"
                  name="time_served"
                  value={formData.time_served}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 5 years"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Trade Qualifications
                </label>
                <input
                  type="text"
                  name="trade_qualifications"
                  value={formData.trade_qualifications}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="List your qualifications"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Trades Competency
                </label>
                <textarea
                  name="trades_competency"
                  value={formData.trades_competency}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="List your trades in order of expertise"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center shadow-lg`}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-slate-300">Professional references and portfolio</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  References
                </label>
                <textarea
                  name="references_text"
                  value={formData.references_text}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Provide professional references"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Proof of Work References (website, google business page)
                </label>
                <input
                  type="text"
                  name="proof_of_work"
                  value={formData.proof_of_work}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Links to your work portfolio"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Social Media Links (Instagram, Facebook, TikTok)
                </label>
                <input
                  type="text"
                  name="socials"
                  value={formData.socials}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Your social media or business profiles"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center shadow-lg`}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-slate-300">Your preferred project types and rates</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Preferred Project Type
                </label>
                <select
                  name="preferred_project_type"
                  value={formData.preferred_project_type}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">Select Project Type</option>
                  <option value="Kitchens">Kitchens</option>
                  <option value="Bathrooms">Bathrooms</option>
                  <option value="Full Renovations">Full Renovations</option>
                  <option value="Extensions">Extensions</option>
                  <option value="New Builds">New Builds</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Max Project Value
                </label>
                <input
                  type="number"
                  name="max_project_value"
                  value={formData.max_project_value}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter maximum project value"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Preferred Pay Type
                </label>
                <select
                  name="pricing_preference"
                  value={formData.pricing_preference}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="day_rate">Day Rate</option>
                  <option value="price">Price</option>
                </select>
              </div>

              {formData.pricing_preference === 'day_rate' && (
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Day Rate
                  </label>
                  <input
                    type="number"
                    name="scope_day_rate"
                    value={formData.scope_day_rate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your day rate"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center shadow-lg`}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-slate-300">Construction Skills Certification Scheme</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  CSCS Registration
                </label>
                <input
                  type="text"
                  name="cscs_registration"
                  value={formData.cscs_registration}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your CSCS registration"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  CSCS Card Number
                </label>
                <input
                  type="text"
                  name="cscs_card_number"
                  value={formData.cscs_card_number}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your CSCS card number"
                />
              </div>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center shadow-lg`}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-slate-300">Payment and job value information</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Bank Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter bank name"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Sort Code <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="sort_code"
                    value={formData.sort_code}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter sort code"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Account Number <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter account number"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Name on Account <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name_on_account"
                  value={formData.name_on_account}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter name on account"
                  required
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Minimum Job Value <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    name="minimum_job_value"
                    value={formData.minimum_job_value}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Min value"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Maximum Job Value <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    name="maximum_job_value"
                    value={formData.maximum_job_value}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Max value"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Day Rate <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    name="day_rate"
                    value={formData.day_rate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Day rate"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center shadow-lg`}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-slate-300">Insurance and qualification documents</p>
            </div>

            <div className="space-y-6">
              <MultiFileInput
                label="Copy of Insurances (screenshot)"
                name="insurances"
                value={formData.insurances}
                onChange={(files) => setFormData((prev) => ({ ...prev, insurances: files }))}
                accept="image/*,.pdf"
                maxFiles={10}
                required
                hint="Add files below; remove any with the × button."
              />
              <MultiFileInput
                label="Copies of Qualifying Bodies Recognition (screenshot)"
                name="qualifying_bodies"
                value={formData.qualifying_bodies}
                onChange={(files) => setFormData((prev) => ({ ...prev, qualifying_bodies: files }))}
                accept="image/*,.pdf"
                maxFiles={10}
                hint="Add files below; remove any with the × button."
              />
            </div>
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center shadow-lg`}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-slate-300 max-w-xl mx-auto">When working for Duke Renovations, you will be expected to wear branded work clothing in the form of a branded high visibility vest. If working for Duke Renovations for any period over 90 days, you will be issued with branded work uniforms. Size options below.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Waist Size</label>
                <input
                  type="text"
                  name="waist_size"
                  value={formData.waist_size}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your waist size"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">Jumper Size</label>
                <input
                  type="text"
                  name="jumper_size"
                  value={formData.jumper_size}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your jumper size"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">T-shirt Size</label>
                <input
                  type="text"
                  name="tshirt_size"
                  value={formData.tshirt_size}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your t-shirt size"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">Gillet Size</label>
                <input
                  type="text"
                  name="gillet_size"
                  value={formData.gillet_size}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your gillet size"
                />
              </div>
            </div>
          </div>
        );

      case 10:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center shadow-lg`}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {currentStepData.title}
              </h2>
              <p className="text-slate-300 max-w-xl mx-auto">
                When it&apos;s our round, let us know how you like your drink.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Preferred drink</label>
                <input
                  type="text"
                  name="preferred_drink"
                  value={formData.preferred_drink}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Tea, Coffee, Orange juice"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">How you like it</label>
                <input
                  type="text"
                  name="drink_notes"
                  value={formData.drink_notes}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Sugar, milk, no milk, etc."
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Navigation />
        <div className="container mx-auto px-4 py-12">
          <div className="text-white text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navigation />
      <div className="container mx-auto px-4 py-12 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-white mb-4">Onboarding</h1>
            
            {/* Modern Progress Indicator */}
            <div className="glass-card p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-white">
                    {steps[currentStep].title}
                  </h2>
                  <p className="text-sm text-slate-400">
                    Step {currentStep + 1} of {steps.length}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-400">
                    Step {currentStep + 1} of {steps.length}
                  </div>
                  <div className="text-xs text-slate-400">
                    {currentStep + 1 === steps.length ? 'Final section' : 'Current section'}
                  </div>
                </div>
              </div>
              
              {/* Progress Bar - shows which section you're on, not completion */}
              <div className="w-full bg-slate-700/50 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1.5">You can fill sections in any order. Use the dots below to jump between sections.</p>
              
              {/* Step Navigation Dots (Mobile-friendly) */}
              <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = index === currentStep;
                  const isCompleted = index < currentStep;
                  
                  return (
                    <button
                      key={step.id}
                      onClick={() => setCurrentStep(index)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                        isActive
                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                          : isCompleted
                          ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                          : 'bg-slate-700/30 text-slate-400 border border-slate-600/30'
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${isActive ? 'text-blue-400' : isCompleted ? 'text-green-400' : 'text-slate-400'}`} />
                      <span className="text-xs font-medium hidden sm:inline">
                        {index + 1}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 flex items-center gap-2">
              <FiAlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-200">
              {success}
            </div>
          )}

          <div className="glass-card p-6 md:p-8 mb-6">{renderFormStep()}</div>

          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <button
              onClick={saveProgress}
              disabled={saving}
              className="px-6 py-3 glass-button text-white font-semibold rounded-lg hover:bg-white/30 flex items-center gap-2 disabled:opacity-50"
            >
              <FiSave className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Progress'}
            </button>

            <div className="flex gap-4">
              <button
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
                className="px-6 py-3 glass-button text-white font-semibold rounded-lg hover:bg-white/30 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiChevronLeft className="w-5 h-5" />
                Back
              </button>

              {currentStep < steps.length - 1 ? (
                <button
                  onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 flex items-center gap-2"
                >
                  Next
                  <FiChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit() || submitting}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiCheck className="w-5 h-5" />
                  {submitting ? 'Submitting...' : 'Submit Onboarding'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
